import { useState } from 'react';
import { MessageDescriptor } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { Trans, useLingui } from '@lingui/react/macro';

import { Endpoints } from '@/api';
import Button from '@/design_system/Button';
import Dialog from '@/design_system/Dialog';
import { Footer, FooterButtons } from '@/design_system/Footer';
import { Label } from '@/design_system/Label/Label';
import { RadioBox, RadioBoxGroup } from '@/design_system/Radio/RadioBox/RadioBox';
import Stack from '@/design_system/Stack';
import TextArea from '@/design_system/TextArea';

const QUALITY_CONTROL_CHOICES = [
  {
    id: 'ko-keep-workshop',
    label: msg({
      id: 'quality_control_ko_choice.ko-keep-workshop.title',
      message: 'Handle in workshop',
    }),
    text: msg({
      id: 'quality_control_ko_choice.ko-keep-workshop.label',
      message: 'Keep the item in the workshop to reassess and complete the service',
    }),
  },
  {
    id: 'ko-new-service-choice',
    label: msg({
      id: 'quality_control_ko_choice.ko-new-service-choice.title',
      message: 'Escalate for review',
    }),
    text: msg({
      id: 'quality_control_ko_choice.ko-new-service-choice.label',
      message: 'Transfer request to determine next steps',
    }),
  },
] satisfies {
  id: 'ko-keep-workshop' | 'ko-new-service-choice' | undefined;
  text: MessageDescriptor;
  label: MessageDescriptor;
}[];

type QualityControlChoice = (typeof QUALITY_CONTROL_CHOICES)[number]['id'];

export const QualityControlKOChoiceDialog = ({
  isOpen,
  onClose,
  onConfirm,
  withChoice,
  isLoading,
}: {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (data: Endpoints['POST /articles/:id/complete-quality-control']['body']) => void;
  withChoice?: boolean;
  isLoading: boolean;
}) => {
  return (
    <Dialog
      isOpen={isOpen}
      title={<Trans id="quality_control_ko_choice.title">Quality Control issue</Trans>}
      onOpenChange={onClose}
    >
      <QualityControlChoiceForm
        onConfirm={(data) => {
          onConfirm(data);
          onClose();
        }}
        withChoice={withChoice}
        isLoading={isLoading}
      />
    </Dialog>
  );
};

const QualityControlChoiceForm = ({
  onConfirm,
  withChoice,
  isLoading,
}: {
  onConfirm: (data: Endpoints['POST /articles/:id/complete-quality-control']['body']) => void;
  withChoice?: boolean;
  isLoading: boolean;
}) => {
  const { t } = useLingui();
  const [showErrors, setShowErrors] = useState(false);

  const [koType, setKoType] = useState<QualityControlChoice | undefined>(
    withChoice ? undefined : 'ko-new-service-choice'
  );
  const [koNote, setKoNote] = useState<string | undefined>();

  const label = withChoice
    ? t({
        id: 'article.actions.quality_control_ko_choice.multi.label',
        message: "You've just reported an issue during quality control. What would you like to do?",
      })
    : t({
        id: 'article.actions.quality_control_ko_choice.label',
        message:
          "You've reported a quality control issue. Add a note to help determine the next steps.",
      });

  return (
    <>
      <main>
        <Stack gap="1rem">
          <Label label={label} fontWeight="regular">
            {withChoice && (
              <RadioBoxGroup
                name="quality-control-choice"
                value={koType}
                onChange={setKoType}
                direction="column"
                isInvalid={showErrors && !koType}
                ariaLabel={t({
                  id: 'quality_control_ko_choice.text',
                  message: 'Choose quality control resolution',
                })}
              >
                {QUALITY_CONTROL_CHOICES.map(({ id, label, text }) => (
                  <RadioBox key={id} value={id} ariaLabel={t(label)} title={t(label)}>
                    {t(text)}
                  </RadioBox>
                ))}
              </RadioBoxGroup>
            )}
          </Label>
          <TextArea
            label={
              withChoice ? (
                <Trans id="quality_control_ko_choice.comment_title">Issue note</Trans>
              ) : undefined
            }
            ariaLabel={t({
              id: 'quality_control_ko_choice.comment_placeholder',
              message: 'Add a note',
            })}
            value={koNote}
            rows={3}
            onChange={(event) => setKoNote(event.target.value)}
            placeholder={t({
              id: 'quality_control_ko_choice.comment_placeholder',
              message: 'Add a note',
            })}
            error={
              showErrors && !koNote
                ? t({
                    id: 'quality_control_ko_choice.note-error',
                    message: 'Please write a note',
                  })
                : undefined
            }
          />
        </Stack>
      </main>
      <Footer>
        <FooterButtons>
          <Button
            variant="danger"
            size="medium"
            onPress={() => {
              setShowErrors(true);

              if (!koType || !koNote) {
                return;
              }

              onConfirm({ koType, koNote });
            }}
            isLoading={isLoading}
          >
            <span style={{ textWrap: 'nowrap' }}>
              {withChoice ? (
                <Trans id="quality_control_ko_choice.choice.confirm">Confirm decision</Trans>
              ) : (
                <Trans id="quality_control_ko_choice.no-choice.confirm">Report issue</Trans>
              )}
            </span>
          </Button>
        </FooterButtons>
      </Footer>
    </>
  );
};
